import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Warehouse, AlertTriangle, TrendingDown, Download, PackageX, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUCKET_COLORS = {
  '0-90': '#16a34a',
  '91-180': '#eab308',
  '181-270': '#f97316',
  '271-365': '#dc2626',
  '365+': '#7f1d1d',
};
const LINE_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#a855f7'];
const ALL_WAREHOUSES = [
  { platform: 'amazon_uae', platform_name: 'Amazon UAE' },
  { platform: 'amazon_ksa', platform_name: 'Amazon KSA' },
  { platform: 'amazon_au', platform_name: 'Amazon AU' },
  { platform: 'noon_uae', platform_name: 'Noon UAE' },
];

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtTs = (iso) => {
  if (!iso) return 'n/a';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
};

export const StockAgingPanel = () => {
  const [summary, setSummary] = useState(null);
  const [cleared, setCleared] = useState(null);
  const [clearedDays, setClearedDays] = useState('30');
  const [trend, setTrend] = useState(null);
  const [liquidation, setLiquidation] = useState(null);
  const [liqBucket, setLiqBucket] = useState('270+');
  const [liqGroupBy, setLiqGroupBy] = useState('sku');
  const [liqCompareOn, setLiqCompareOn] = useState(false);
  const [liqCompareData, setLiqCompareData] = useState(null);
  const [histDates, setHistDates] = useState([]);
  const [cmpFrom, setCmpFrom] = useState('');
  const [cmpTo, setCmpTo] = useState('');
  const [cmpThreshold, setCmpThreshold] = useState('90+');
  const [compare, setCompare] = useState(null);
  const [view, setView] = useState('all'); // 'all' or a platform key
  const [loading, setLoading] = useState(true);

  const pq = (platform) => (platform && platform !== 'all' ? `&platform=${platform}` : '');

  const fetchCleared = useCallback(async (days, platform) => {
    const r = await axios.get(`${API}/aging/cleared?days=${days}${pq(platform)}`);
    setCleared(r.data);
  }, []);

  const fetchLiquidation = useCallback(async (bucket, platform, groupBy) => {
    const r = await axios.get(`${API}/aging/liquidation?bucket=${encodeURIComponent(bucket)}&group_by=${groupBy}${pq(platform)}`);
    setLiquidation(r.data);
  }, []);

  const fetchCompare = useCallback(async (from, to, platform, threshold) => {
    if (!from || !to) { setCompare(null); return; }
    const r = await axios.get(`${API}/aging/compare?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&age_threshold=${encodeURIComponent(threshold)}${pq(platform)}`);
    setCompare(r.data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [s, t, hd] = await Promise.all([
          axios.get(`${API}/aging/summary`),
          axios.get(`${API}/aging/trend`),
          axios.get(`${API}/aging/history-dates`),
        ]);
        setSummary(s.data);
        setTrend(t.data);
        const dates = hd.data?.dates || [];
        setHistDates(dates);
        if (dates.length >= 2) {
          setCmpFrom(dates[0]);
          setCmpTo(dates[dates.length - 1]);
        }
        await fetchCleared(clearedDays, view);
        await fetchLiquidation(liqBucket, view, liqGroupBy);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch comparison whenever the two dates (or warehouse/threshold) change
  useEffect(() => {
    if (cmpFrom && cmpTo) fetchCompare(cmpFrom, cmpTo, view, cmpThreshold);
  }, [cmpFrom, cmpTo, view, cmpThreshold, fetchCompare]);

  // SKU-wise liquidation comparison
  const fetchLiqCompare = useCallback(async (from, to, platform, bucket, groupBy) => {
    if (!from || !to) { setLiqCompareData(null); return; }
    const r = await axios.get(`${API}/aging/liquidation/compare?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucket=${encodeURIComponent(bucket)}&group_by=${groupBy}${pq(platform)}`);
    setLiqCompareData(r.data);
  }, []);

  useEffect(() => {
    if (liqCompareOn && cmpFrom && cmpTo) fetchLiqCompare(cmpFrom, cmpTo, view, liqBucket, liqGroupBy);
  }, [liqCompareOn, cmpFrom, cmpTo, view, liqBucket, liqGroupBy, fetchLiqCompare]);

  const onClearedDays = (v) => { setClearedDays(v); fetchCleared(v, view); };
  const onLiqBucket = (v) => { setLiqBucket(v); fetchLiquidation(v, view, liqGroupBy); };
  const onLiqGroupBy = (v) => { setLiqGroupBy(v); fetchLiquidation(liqBucket, view, v); };
  const onView = (v) => { setView(v); fetchCleared(clearedDays, v); fetchLiquidation(liqBucket, v, liqGroupBy); };

  const exportLiquidation = async () => {
    const url = liqCompareOn && cmpFrom && cmpTo
      ? `${API}/aging/liquidation/compare/export?from=${encodeURIComponent(cmpFrom)}&to=${encodeURIComponent(cmpTo)}&bucket=${encodeURIComponent(liqBucket)}&group_by=${liqGroupBy}${pq(view)}`
      : `${API}/aging/liquidation/export?bucket=${encodeURIComponent(liqBucket)}&group_by=${liqGroupBy}${pq(view)}`;
    const res = await axios.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = liqCompareOn
      ? `liquidation_compare_${liqGroupBy}_${liqBucket.replace('+', 'plus')}_${cmpFrom}_to_${cmpTo}.csv`
      : `liquidation_${liqGroupBy}_${liqBucket.replace('+', 'plus')}.csv`;
    a.click(); window.URL.revokeObjectURL(blobUrl);
  };

  if (loading) return null;

  // Merge backend warehouses with the canonical list so every warehouse is visible
  const backendMap = {};
  (summary?.warehouses || []).forEach((w) => { backendMap[w.platform] = w; });
  const warehouses = ALL_WAREHOUSES.map((base) => backendMap[base.platform] || {
    ...base, has_data: false, snapshot_date: null, last_refreshed: null,
    total_quantity: 0, total_value: 0, aged_90_plus_quantity: 0, aged_90_plus_value: 0,
    aged_90_plus_pct: 0, buckets: [],
  });
  const hasAny = warehouses.some((w) => w.has_data);

  const selectedWh = view === 'all' ? null : warehouses.find((w) => w.platform === view);
  const stats = view === 'all' ? (summary?.totals || {}) : (selectedWh || {});
  const lastRefreshed = view === 'all' ? summary?.totals?.last_refreshed : selectedWh?.last_refreshed;
  const cardWarehouses = view === 'all' ? warehouses : (selectedWh ? [selectedWh] : []);

  // Build trend chart data: [{date, amazon_ksa: value, ...}]
  const trendDates = {};
  (trend?.warehouses || []).forEach((w) => {
    if (view !== 'all' && w.platform !== view) return;
    (w.points || []).forEach((p) => {
      trendDates[p.snapshot_date] = trendDates[p.snapshot_date] || { date: p.snapshot_date };
      trendDates[p.snapshot_date][w.platform] = p.aged_90_plus_value;
    });
  });
  const trendData = Object.values(trendDates).sort((a, b) => (a.date > b.date ? 1 : -1));
  const trendWarehouses = (trend?.warehouses || []).filter((w) => (w.points || []).length > 0 && (view === 'all' || w.platform === view));

  return (
    <div className="border border-border bg-card rounded-sm p-5 space-y-6" data-testid="stock-aging-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Warehouse className="w-5 h-5" strokeWidth={1.5} />
        <h2 className="text-lg font-bold font-heading">Stock Aging &amp; Dead Stock</h2>
        <Badge variant="outline" className="ml-1">Goal: clear within 90 days</Badge>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden sm:inline">Warehouse</span>
          <Select value={view} onValueChange={onView}>
            <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="aging-warehouse-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Warehouses (Overall)</SelectItem>
              {ALL_WAREHOUSES.map((w) => (
                <SelectItem key={w.platform} value={w.platform}>
                  {w.platform_name}{!backendMap[w.platform]?.has_data ? ' — no data' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Data freshness line */}
      {hasAny && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground -mt-3" data-testid="aging-freshness">
          <Clock className="w-3 h-3" />
          <span>
            Last refreshed: <b className="text-foreground">{fmtTs(lastRefreshed)}</b>
            {view !== 'all' && (
              <> · report snapshot date: <b className="text-foreground">{selectedWh?.snapshot_date || 'n/a'}</b></>
            )}
          </span>
        </div>
      )}

      {!hasAny && (
        <p className="text-sm text-muted-foreground" data-testid="aging-no-data">
          No inventory-age data uploaded yet. Upload FBA Inventory Aged reports from the <b>Deal Recommendations</b> page to populate this dashboard.
        </p>
      )}

      {hasAny && (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-border rounded-sm p-4" data-testid="aging-total-value">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                {view === 'all' ? 'Total Stock Value' : `${selectedWh?.platform_name} Stock Value`}
              </p>
              <p className="text-2xl font-bold font-mono">{inr(stats.total_value)}</p>
              <p className="text-xs text-muted-foreground mt-1">{Number(stats.total_quantity || 0).toLocaleString()} units</p>
            </div>
            <div className="border border-border rounded-sm p-4 bg-destructive/5" data-testid="aging-dead-value">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-destructive" /> Dead Stock (90+ days)
              </p>
              <p className="text-2xl font-bold font-mono text-destructive">{inr(stats.aged_90_plus_value)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {Number(stats.aged_90_plus_quantity || 0).toLocaleString()} units · {stats.aged_90_plus_pct || 0}% of stock
              </p>
            </div>
            <div className="border border-border rounded-sm p-4 bg-green-500/5" data-testid="aging-cleared-value">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <TrendingDown className="w-3 h-3 text-green-600" /> Dead Stock Cleared
                </p>
                <Select value={clearedDays} onValueChange={onClearedDays}>
                  <SelectTrigger className="h-6 w-[92px] text-xs" data-testid="cleared-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7d</SelectItem>
                    <SelectItem value="30">Last 30d</SelectItem>
                    <SelectItem value="60">Last 60d</SelectItem>
                    <SelectItem value="90">Last 90d</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-2xl font-bold font-mono text-green-700" data-testid="cleared-total-value">{inr(cleared?.total_cleared_value)}</p>
              <p className="text-xs text-muted-foreground mt-1">{(cleared?.total_cleared_units || 0).toLocaleString()} aged units sold</p>
              {cleared?.total_cleared_by_bracket && cleared.total_cleared_units > 0 && (
                <div className="mt-2 pt-2 border-t border-border/60 space-y-1" data-testid="cleared-by-bracket">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Cleared by age bracket (oldest first)</p>
                  {(cleared.brackets || ['365+', '271-365', '181-270', '91-180']).map((b) => {
                    const item = cleared.total_cleared_by_bracket[b] || { units: 0, value: 0 };
                    return (
                      <div key={b} className="flex items-center justify-between text-xs" data-testid={`cleared-bracket-${b}`}>
                        <span className="text-muted-foreground">{b} days</span>
                        <span className="font-mono">{item.units.toLocaleString()}u · {inr(item.value)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border border-border rounded-sm p-4" data-testid="aging-liquidation-value">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                <PackageX className="w-3 h-3" /> Liquidation ({liqBucket})
              </p>
              <p className="text-2xl font-bold font-mono">{inr(liquidation?.total_value)}</p>
              <p className="text-xs text-muted-foreground mt-1">{(liquidation?.total_quantity || 0).toLocaleString()} units · {liquidation?.count || 0} SKUs</p>
            </div>
          </div>

          {/* Per-warehouse breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {cardWarehouses.map((w) => (
              w.has_data ? (
                <div key={w.platform} className="border border-border rounded-sm p-4" data-testid={`aging-wh-${w.platform}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">{w.platform_name}</p>
                    <span className="text-xs text-muted-foreground text-right">
                      snapshot {w.snapshot_date || 'n/a'}
                      <br />
                      <span className="text-[10px]">refreshed {fmtTs(w.last_refreshed)}</span>
                    </span>
                  </div>
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="font-mono text-lg font-bold">{inr(w.total_value)}</span>
                    <span className="text-xs text-muted-foreground">{w.total_quantity.toLocaleString()} units</span>
                    <span className="text-xs text-destructive ml-auto font-medium">{w.aged_90_plus_pct}% dead</span>
                  </div>
                  {/* Stacked bucket bar */}
                  <div className="flex h-4 w-full overflow-hidden rounded-sm mb-2">
                    {w.buckets.map((b) => b.quantity > 0 && (
                      <div
                        key={b.bucket}
                        style={{ width: `${b.qty_pct}%`, backgroundColor: BUCKET_COLORS[b.bucket] }}
                        title={`${b.bucket} days: ${b.quantity} units (${b.qty_pct}%) · ${inr(b.value)}`}
                      />
                    ))}
                  </div>
                  <div className="grid grid-cols-5 gap-1 text-[10px]">
                    {w.buckets.map((b) => (
                      <div key={b.bucket} className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: BUCKET_COLORS[b.bucket] }} />
                          <span className="text-muted-foreground">{b.bucket}</span>
                        </div>
                        <div className="font-mono">{b.qty_pct}%</div>
                        <div className="font-mono text-muted-foreground">{b.quantity}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div key={w.platform} className="border border-dashed border-border rounded-sm p-4 flex flex-col items-center justify-center text-center min-h-[120px]" data-testid={`aging-wh-${w.platform}-empty`}>
                  <p className="font-medium text-muted-foreground">{w.platform_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">No inventory-age report uploaded for this warehouse yet.</p>
                </div>
              )
            ))}
          </div>

          {/* Cleared per warehouse */}
          {cleared && (
            <div className="flex flex-wrap gap-2" data-testid="cleared-by-warehouse">
              {cleared.warehouses.filter((w) => w.cleared_units > 0).map((w) => (
                <Badge key={w.platform} variant="outline" className="text-green-700 border-green-300">
                  {w.platform_name}: {w.cleared_units} units · {inr(w.cleared_value)} cleared
                </Badge>
              ))}
              {cleared.total_cleared_units === 0 && (
                <span className="text-xs text-muted-foreground">No aged units cleared in this period.</span>
              )}
            </div>
          )}

          {/* Trend chart */}
          <div>
            <p className="text-sm font-medium mb-2">Dead Stock (90+ days) Value Trend</p>
            {trendData.length > 1 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trendData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend />
                  {trendWarehouses.map((w, i) => (
                    <Line key={w.platform} type="monotone" dataKey={w.platform} name={w.platform_name}
                          stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="trend-insufficient">
                Trend builds up over time. Upload aging reports on different dates to see how dead stock changes week over week (currently {trendData.length} snapshot).
              </p>
            )}
          </div>

          {/* Period-over-period comparison */}
          <div data-testid="aging-compare-section">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <p className="text-sm font-medium">Aging Comparison — Period vs Period</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">Age</span>
                <Select value={cmpThreshold} onValueChange={setCmpThreshold}>
                  <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="compare-threshold-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="90+">90+ days</SelectItem>
                    <SelectItem value="180+">180+ days</SelectItem>
                    <SelectItem value="270+">270+ days</SelectItem>
                    <SelectItem value="365+">365+ days</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">From</span>
                <Select value={cmpFrom} onValueChange={setCmpFrom} disabled={histDates.length < 1}>
                  <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="compare-from-select">
                    <SelectValue placeholder="snapshot" />
                  </SelectTrigger>
                  <SelectContent>
                    {histDates.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">to</span>
                <Select value={cmpTo} onValueChange={setCmpTo} disabled={histDates.length < 1}>
                  <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="compare-to-select">
                    <SelectValue placeholder="snapshot" />
                  </SelectTrigger>
                  <SelectContent>
                    {histDates.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {histDates.length < 2 ? (
              <p className="text-xs text-muted-foreground" data-testid="compare-insufficient">
                Only {histDates.length} aging snapshot available. Upload FBA Inventory Aged reports on different dates (from the Deal Recommendations page) to compare how much dead stock you've cleared between periods.
              </p>
            ) : compare && (
              <div className="space-y-3" data-testid="compare-results">
                {/* Overall delta strip */}
                {(() => {
                  const d = compare.totals.delta_aged_value;
                  const dq = compare.totals.delta_aged_qty;
                  const improved = d != null && d < 0;
                  return (
                    <div className={`border rounded-sm p-4 ${improved ? 'bg-green-500/5 border-green-300' : 'bg-destructive/5 border-destructive/40'}`} data-testid="compare-overall">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                        Aged Stock ({cmpThreshold}) change · {view === 'all' ? 'All Warehouses' : selectedWh?.platform_name} · {compare.from_date} → {compare.to_date}
                      </p>
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <span className={`text-2xl font-bold font-mono ${improved ? 'text-green-700' : 'text-destructive'}`}>
                          {d > 0 ? '+' : ''}{inr(d)}
                        </span>
                        <span className={`text-sm font-mono ${improved ? 'text-green-700' : 'text-destructive'}`}>
                          {dq > 0 ? '+' : ''}{Number(dq || 0).toLocaleString()} units
                        </span>
                        {compare.totals.delta_aged_pct != null && (
                          <span className="text-xs text-muted-foreground">({compare.totals.delta_aged_pct > 0 ? '+' : ''}{compare.totals.delta_aged_pct}%)</span>
                        )}
                        <span className="text-xs text-muted-foreground ml-auto">
                          {improved ? 'Dead stock reduced 🎉' : 'Dead stock increased'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {compare.from_date}: {inr(compare.totals.from.aged_value)} ({Number(compare.totals.from.aged_quantity).toLocaleString()}u)
                        {'  →  '}
                        {compare.to_date}: {inr(compare.totals.to.aged_value)} ({Number(compare.totals.to.aged_quantity).toLocaleString()}u)
                      </p>
                    </div>
                  );
                })()}

                {/* Per-warehouse deltas */}
                <div className="flex flex-wrap gap-2">
                  {compare.warehouses.map((w) => (
                    <Badge key={w.platform} variant="outline"
                           className={w.has_both ? (w.delta_aged_value < 0 ? 'text-green-700 border-green-300' : 'text-destructive border-destructive/40') : 'text-muted-foreground'}
                           data-testid={`compare-wh-${w.platform}`}>
                      {w.platform_name}: {w.has_both
                        ? `${w.delta_aged_value > 0 ? '+' : ''}${inr(w.delta_aged_value)} (${w.delta_aged_qty > 0 ? '+' : ''}${w.delta_aged_qty}u)`
                        : 'no snapshot on both dates'}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Liquidation list */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex flex-col">
                  <p className="text-sm font-medium">Removal / Liquidation List</p>
                  <p className="text-[11px] text-muted-foreground" data-testid="liquidation-snapshot-note">
                    {liqCompareOn
                      ? `Comparing report snapshots ${cmpFrom} → ${cmpTo}`
                      : (view === 'all'
                          ? `Latest report per warehouse (${warehouses.filter((w) => w.has_data).map((w) => `${w.platform_name}: ${w.snapshot_date || 'n/a'}`).join(', ') || 'no data'})`
                          : `As per ${selectedWh?.platform_name} report dated ${selectedWh?.snapshot_date || 'n/a'}`)}
                  </p>
                </div>
                <Button
                  variant={liqCompareOn ? 'default' : 'outline'}
                  size="sm"
                  disabled={histDates.length < 2}
                  onClick={() => setLiqCompareOn((v) => !v)}
                  data-testid="liquidation-compare-toggle"
                  title={histDates.length < 2 ? 'Need 2+ aging snapshots to compare' : 'Show SKU-wise change between the two selected snapshots'}
                >
                  {liqCompareOn ? `Comparing ${cmpFrom} → ${cmpTo}` : 'Compare periods'}
                </Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={liqGroupBy} onValueChange={onLiqGroupBy}>
                  <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="liquidation-groupby-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sku">By Child SKU</SelectItem>
                    <SelectItem value="color_parent">By Color Parent</SelectItem>
                    <SelectItem value="parent_sku">By Parent SKU</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={liqBucket} onValueChange={onLiqBucket}>
                  <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="liquidation-bucket-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="270+">Aged 270+ days</SelectItem>
                    <SelectItem value="365+">Aged 365+ days</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={exportLiquidation} data-testid="liquidation-export-btn">
                  <Download className="w-3 h-3 mr-1" /> Export
                </Button>
              </div>
            </div>
            <div className="border border-border rounded-sm overflow-hidden">
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                {liqCompareOn ? (
                  <table className="w-full text-sm" data-testid="liquidation-compare-table">
                    <thead className="bg-muted/50 border-b border-border sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-xs uppercase tracking-widest font-medium">
                          {liqGroupBy === 'color_parent' ? 'Color Parent' : liqGroupBy === 'parent_sku' ? 'Parent SKU' : 'SKU'}
                        </th>
                        <th className="text-right p-2 text-xs uppercase tracking-widest font-medium">{cmpFrom} Qty</th>
                        <th className="text-right p-2 text-xs uppercase tracking-widest font-medium">{cmpTo} Qty</th>
                        <th className="text-right p-2 text-xs uppercase tracking-widest font-medium">Δ Qty</th>
                        <th className="text-right p-2 text-xs uppercase tracking-widest font-medium">Δ Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(liqCompareData?.items || []).slice(0, 300).map((i, idx) => {
                        const improved = i.delta_quantity < 0;
                        return (
                          <tr key={`${i.group_key}-${idx}`} className="border-b border-border hover:bg-muted/30" data-testid={`liquidation-compare-row-${i.group_key}`}>
                            <td className="p-2 font-mono text-xs">
                              {i.group_key}
                              {liqGroupBy !== 'sku' && <span className="ml-2 text-[10px] text-muted-foreground">({i.sku_count} SKUs)</span>}
                            </td>
                            <td className="p-2 text-right font-mono text-muted-foreground">{i.from_quantity}</td>
                            <td className="p-2 text-right font-mono">{i.to_quantity}</td>
                            <td className={`p-2 text-right font-mono font-medium ${i.delta_quantity === 0 ? 'text-muted-foreground' : improved ? 'text-green-700' : 'text-destructive'}`}>
                              {i.delta_quantity > 0 ? '+' : ''}{i.delta_quantity}
                            </td>
                            <td className={`p-2 text-right font-mono ${i.delta_value === 0 ? 'text-muted-foreground' : i.delta_value < 0 ? 'text-green-700' : 'text-destructive'}`}>
                              {i.delta_value > 0 ? '+' : ''}{inr(i.delta_value)}
                            </td>
                          </tr>
                        );
                      })}
                      {(!liqCompareData || liqCompareData.items.length === 0) && (
                        <tr><td colSpan="5" className="p-6 text-center text-muted-foreground text-sm">No aged stock in either snapshot for this scope.</td></tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                <table className="w-full text-sm" data-testid="liquidation-table">
                  <thead className="bg-muted/50 border-b border-border sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs uppercase tracking-widest font-medium">
                        {liqGroupBy === 'color_parent' ? 'Color Parent' : liqGroupBy === 'parent_sku' ? 'Parent SKU' : 'SKU'}
                      </th>
                      <th className="text-left p-2 text-xs uppercase tracking-widest font-medium">{liqGroupBy === 'sku' ? 'Warehouse' : 'Scope'}</th>
                      <th className="text-right p-2 text-xs uppercase tracking-widest font-medium">271-365</th>
                      <th className="text-right p-2 text-xs uppercase tracking-widest font-medium">365+</th>
                      <th className="text-right p-2 text-xs uppercase tracking-widest font-medium">Old Qty</th>
                      <th className="text-right p-2 text-xs uppercase tracking-widest font-medium">Cost Value</th>
                      <th className="text-right p-2 text-xs uppercase tracking-widest font-medium">Vel (90d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(liquidation?.items || []).slice(0, 200).map((i, idx) => (
                      <tr key={`${i.group_key}-${idx}`} className="border-b border-border hover:bg-muted/30" data-testid={`liquidation-row-${i.group_key}`}>
                        <td className="p-2 font-mono text-xs">
                          {i.group_key}
                          {liqGroupBy !== 'sku' && (
                            <span className="ml-2 text-[10px] text-muted-foreground">({i.sku_count} SKUs)</span>
                          )}
                        </td>
                        <td className="p-2 text-xs text-muted-foreground">{i.platform_name}</td>
                        <td className="p-2 text-right font-mono">{i.age_271_365}</td>
                        <td className="p-2 text-right font-mono">{i.age_365_plus}</td>
                        <td className="p-2 text-right font-mono font-medium">{i.old_quantity}</td>
                        <td className="p-2 text-right font-mono">{inr(i.value)}</td>
                        <td className="p-2 text-right font-mono text-muted-foreground">{i.velocity_90d}</td>
                      </tr>
                    ))}
                    {(!liquidation || liquidation.items.length === 0) && (
                      <tr><td colSpan="7" className="p-6 text-center text-muted-foreground text-sm">No stock in this age range 🎉</td></tr>
                    )}
                  </tbody>
                </table>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
